import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAvailableNumbers } from "@/features/number-requests/hooks/use-number-requests";
import type {
  AvailableNumber,
  AvailableNumberType,
} from "@/features/number-requests/types";
import { useDebounce } from "@/features/utils/use-debounce";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { formatPhoneNumber } from "react-phone-number-input";
import { useParams } from "react-router-dom";

export type NumberOffer = "loading" | "offered" | "none";

/** The mobile ranges a private person is offered. */
const MOBILE_PREFIXES = ["06", "07"];

/**
 * Picking the number itself.
 *
 * The search starts where the server suggests — the company's region for a
 * geographic number, 06 for a mobile — and the prefix stays editable: typing
 * more digits narrows it ("0491" for Marseille), typing others moves it. The
 * first answer's prefix is copied into the field once, so the client sees what
 * was searched rather than an empty box.
 */
export function NumberStep({
  type,
  country,
  hints,
  prefix,
  onPrefixChange,
  selected,
  onSelect,
  onOfferChange,
}: {
  type: AvailableNumberType;
  country: string;
  /** Where the company is, for the server's first suggestion. */
  hints: { postcode?: string; address?: string };
  /** Undefined until the server has suggested one. */
  prefix: string | undefined;
  onPrefixChange: (prefix: string) => void;
  selected: AvailableNumber | null;
  onSelect: (number: AvailableNumber | null) => void;
  /**
   * Whether there is a choice to make: with numbers on offer one must be
   * picked; with none (nothing for this prefix, no carrier account) the
   * request goes on with the prefix alone. `loading` while it is not known.
   */
  onOfferChange: (offer: NumberOffer) => void;
}) {
  const { t } = useTranslation("numberRequests");
  const { company: clientId } = useParams<{ company: string }>();

  const mobile = type === "mobile" && country === "FR";
  const debouncedPrefix = useDebounce(prefix, 400);
  // A mobile search outside 06/07 is not a mobile search: say so, ask nothing.
  const offRange =
    mobile &&
    (debouncedPrefix || "").length >= 2 &&
    !MOBILE_PREFIXES.some((range) => (debouncedPrefix || "").startsWith(range));

  // The suggestion's own answer stands for its prefix: asking again with the
  // prefix spelled out would be a second search, with other numbers in it.
  const [suggested, setSuggested] = useState<string | undefined>(undefined);
  const searched =
    debouncedPrefix !== undefined && debouncedPrefix !== suggested
      ? debouncedPrefix
      : undefined;

  const { data, isFetching, isError } = useAvailableNumbers(
    clientId || "",
    { type, country, prefix: searched, ...hints },
    { enabled: !offRange },
  );

  useEffect(() => {
    if (prefix === undefined && data) {
      setSuggested(data.prefix);
      onPrefixChange(data.prefix);
    }
  }, [data, prefix, onPrefixChange]);

  // A number picked under another prefix is not one of these any more.
  useEffect(() => {
    if (
      selected &&
      data &&
      !data.numbers.some((n) => n.phone_number === selected.phone_number)
    ) {
      onSelect(null);
    }
  }, [data, selected, onSelect]);

  const numbers = offRange ? [] : data?.numbers || [];
  const shownPrefix = prefix ?? "";

  const offer: NumberOffer = offRange
    ? "none"
    : isFetching || prefix !== debouncedPrefix
      ? "loading"
      : numbers.length
        ? "offered"
        : "none";
  useEffect(() => onOfferChange(offer), [offer, onOfferChange]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="number-prefix">
          {t("numberRequests.create.number.prefixLabel")}
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="number-prefix"
            value={shownPrefix}
            inputMode="numeric"
            maxLength={6}
            onChange={(event) =>
              onPrefixChange(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder={mobile ? "06" : "04"}
            className="w-28 tabular-nums"
          />
          {mobile &&
            MOBILE_PREFIXES.map((range) => (
              <Button
                key={range}
                type="button"
                size="sm"
                variant={shownPrefix.startsWith(range) ? "default" : "outline"}
                onClick={() => onPrefixChange(range)}
              >
                {range}
              </Button>
            ))}
        </div>
        <p className="text-muted-foreground text-sm">
          {offRange
            ? t("numberRequests.create.number.mobileOnly")
            : t(
                mobile
                  ? "numberRequests.create.number.prefixHelpMobile"
                  : "numberRequests.create.number.prefixHelp",
              )}
        </p>
      </div>

      {isFetching && !data ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </div>
      ) : numbers.length > 0 ? (
        <div
          className={cn(
            "grid max-h-72 gap-2 overflow-y-auto sm:grid-cols-2",
            isFetching && "opacity-60",
          )}
          role="radiogroup"
          aria-label={t("numberRequests.create.number.title")}
        >
          {numbers.map((number) => {
            const isSelected = selected?.phone_number === number.phone_number;
            return (
              <button
                key={number.phone_number}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onSelect(number)}
                className={cn(
                  "relative rounded-lg border px-3 py-2 text-left transition-colors",
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "hover:bg-muted/50",
                )}
              >
                <span className="block text-sm font-medium tabular-nums">
                  {formatPhoneNumber(number.phone_number) ||
                    number.friendly_name}
                </span>
                {number.locality && (
                  <span className="text-muted-foreground block text-xs">
                    {number.locality}
                  </span>
                )}
                {isSelected && (
                  <CheckCircleIcon
                    className="text-primary absolute top-2.5 right-2.5 h-4 w-4"
                    weight="fill"
                  />
                )}
              </button>
            );
          })}
        </div>
      ) : (
        !offRange &&
        !isFetching && (
          <p className="bg-muted/50 rounded-lg border p-3 text-sm">
            {isError || data?.searchable === false
              ? t("numberRequests.create.number.unavailable", {
                  prefix: shownPrefix || "—",
                })
              : t("numberRequests.create.number.none", {
                  prefix: shownPrefix || "—",
                })}
          </p>
        )
      )}
    </div>
  );
}
